import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { printSchedule } from "@/lib/printSchedule";
import { Package, Sofa, FileDown, Printer, Plus, Library as LibraryIcon, MoreHorizontal, Lock, LayoutGrid, Pencil, Trash2, ArrowUp, ArrowDown, GripVertical, Upload } from "lucide-react";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const money = (v) => "$" + Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 });
const UNASSIGNED = "__unassigned__";
const STATUS = {
  proposed: { label: "Proposed", color: "#52525B", bg: "#F4F4F5" },
  approved: { label: "Approved", color: "#059669", bg: "#ECFDF5" },
  rejected: { label: "Rejected", color: "#E11D48", bg: "#FFF1F2" },
  ordered: { label: "Ordered", color: "#2563EB", bg: "#EFF6FF" },
};
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—";
const EMPTY = { name: "", supplier: "", product_code: "", category: "", group: "", space_id: "", location: "", finish: "", size: "", qty: 1, unit: "each", rate_ex_gst: 0, status: "proposed", notes: "" };

export default function SchedulesTab({ project, canManage }) {
  const { user } = useAuth();
  const pid = project.project_id;
  const [kind, setKind] = useState("material");
  const [data, setData] = useState({ material: null, ffe: null });
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spaceFilter, setSpaceFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [saveToLib, setSaveToLib] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printOpts, setPrintOpts] = useState({ copy: "client", images: true, issuedFor: "Client approval" });
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragSec = useRef(null);

  const cur = data[kind] || { items: [], meta: { rev: "A" }, summary: {} };

  const loadKind = useCallback(async (k) => {
    const { data: d } = await api.get(`/projects/${pid}/schedules/${k}`);
    setData((prev) => ({ ...prev, [k]: d }));
    if (d.spaces) setSpaces(d.spaces);
    return d;
  }, [pid]);

  useEffect(() => { (async () => { setLoading(true); await Promise.all([loadKind("material"), loadKind("ffe")]); setLoading(false); })(); }, [loadKind]);
  const reload = () => loadKind(kind);

  // Build one section per defined space (shown even when empty) + an Unassigned bucket.
  const sections = useMemo(() => {
    const bucket = {};
    (cur.items || []).forEach((i) => {
      const key = i.space_id || UNASSIGNED;
      (bucket[key] = bucket[key] || []).push(i);
    });
    const secs = spaces.map((s) => ({ id: s.space_id, name: s.name, items: bucket[s.space_id] || [] }));
    if ((bucket[UNASSIGNED] || []).length) secs.push({ id: UNASSIGNED, name: "Unassigned", items: bucket[UNASSIGNED] });
    return secs;
  }, [cur.items, spaces]);

  const visibleSections = useMemo(
    () => spaceFilter === "all" ? sections : sections.filter((s) => s.id === spaceFilter),
    [sections, spaceFilter]);

  const createSpace = async (name) => {
    const { data: sp } = await api.post(`/projects/${pid}/schedules-spaces`, { name });
    setSpaces((prev) => [...prev, sp]);
    return sp;
  };

  const patchRow = async (item, body) => {
    try { await api.patch(`/projects/${pid}/schedules/${kind}/items/${item.item_id}`, body); reload(); }
    catch (e) { toast.error(apiErr(e, "Could not update")); }
  };
  const removeRow = async (item) => {
    try { await api.delete(`/projects/${pid}/schedules/${kind}/items/${item.item_id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error("Could not remove"); }
  };
  const bumpRev = async () => {
    try { await api.post(`/projects/${pid}/schedules/${kind}/revision`); toast.success("Revision bumped"); reload(); }
    catch (e) { toast.error("Could not bump revision"); }
  };

  // Drag-and-drop reorder within a single space.
  const onRowDrop = async (sec, targetItem) => {
    const srcId = dragId;
    setDragId(null);
    if (!srcId || srcId === targetItem.item_id || dragSec.current !== sec.id) return;
    const ids = sec.items.map((i) => i.item_id).filter((x) => x !== srcId);
    const at = ids.indexOf(targetItem.item_id);
    ids.splice(at, 0, srcId);
    try { await api.post(`/projects/${pid}/schedules/${kind}/items/reorder`, { item_ids: ids }); reload(); }
    catch (e) { toast.error("Could not reorder"); }
  };

  const saveEdit = async () => {
    if (!edit.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (edit.item_id) {
        await api.patch(`/projects/${pid}/schedules/${kind}/items/${edit.item_id}`, { ...edit, qty: Number(edit.qty) || 0, rate_ex_gst: Number(edit.rate_ex_gst) || 0 });
      } else {
        let product_id = null;
        if (saveToLib) {
          const { data: p } = await api.post("/library/products", { name: edit.name, category: edit.category || "Other", supplier: edit.supplier, code: edit.product_code, colour: edit.finish, size: edit.size, unit: edit.unit, cost_ex_gst: Number(edit.rate_ex_gst) || 0 });
          product_id = p.product_id;
        }
        await api.post(`/projects/${pid}/schedules/${kind}/items`, { ...edit, product_id, qty: Number(edit.qty) || 0, rate_ex_gst: Number(edit.rate_ex_gst) || 0 });
      }
      setEdit(null); setSaveToLib(false); toast.success("Saved"); reload();
    } catch (e) { toast.error(apiErr(e, "Could not save")); }
  };

  const exportCsv = () => {
    const ffe = kind === "ffe";
    const head = ["Code", "Name", "Supplier", "Product code", "Space", "Location", "Finish", "Size", "Qty", "Unit", ...(ffe ? ["Rate ex GST", "Total"] : []), "Status"];
    const rows = (cur.items || []).map((i) => [i.code, i.name, i.supplier, i.product_code, i.group, i.location, i.finish, i.size, i.qty, i.unit, ...(ffe ? [i.rate_ex_gst, (i.rate_ex_gst || 0) * (i.qty || 0)] : []), i.status]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${project.name} - ${ffe ? "FF&E" : "Material"} schedule Rev ${cur.meta.rev}.csv`;
    a.click();
  };

  const doPrint = () => {
    printSchedule({ project, kind, items: cur.items || [], meta: cur.meta, user, ...printOpts });
    setPrintOpen(false);
  };

  const Card = ({ k, Icon, label }) => {
    const d = data[k] || { summary: {}, meta: { rev: "A" } };
    const s = d.summary || {};
    return (
      <button onClick={() => { setKind(k); setSpaceFilter("all"); }} data-testid={`schedule-card-${k}`}
        className={`flex-1 flex items-center gap-3 border rounded-sm p-4 text-left transition-colors ${kind === k ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-300"}`}>
        <div className="h-11 w-11 rounded-sm bg-zinc-900 text-white flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm">{label}</p>
          <p className="text-xs text-zinc-500">{s.count || 0} items · Rev {d.meta?.rev || "A"} · updated {fmt(d.meta?.updated_at)}</p>
          <div className="flex gap-1.5 mt-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-600">{s.approved_count || 0} approved</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-600">{s.awaiting_count || 0} awaiting</span>
          </div>
        </div>
      </button>
    );
  };

  const ffe = kind === "ffe";
  const gridCols = ffe ? "grid-cols-[40px_90px_1fr_120px_110px_80px_60px_60px_110px_90px_40px]" : "grid-cols-[40px_90px_1fr_120px_110px_80px_60px_80px_110px_40px]";

  return (
    <div data-testid="schedules-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Card k="material" Icon={Package} label="Material schedule" />
        <Card k="ffe" Icon={Sofa} label="FF&E schedule" />
      </div>

      {ffe && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Schedule total ex GST", val: money(cur.summary.subtotal_ex_gst), accent: "text-zinc-900" },
            { label: "Approved by client", val: money((cur.items || []).filter((i) => i.status === "approved").reduce((a, i) => a + (i.rate_ex_gst || 0) * (i.qty || 0), 0)), accent: "text-emerald-600" },
            { label: "Awaiting approval", val: money((cur.items || []).filter((i) => i.status === "proposed").reduce((a, i) => a + (i.rate_ex_gst || 0) * (i.qty || 0), 0)), accent: "text-amber-600" },
            { label: `Items · ${spaces.length} spaces`, val: cur.summary.count || 0, accent: "text-zinc-900" },
          ].map((c, i) => (
            <div key={c.label} className="border border-zinc-200 bg-white p-4" data-testid={`schedule-summary-${i}`}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{c.label}</p>
              <p className={`font-heading text-2xl font-black tracking-tight mt-1 ${c.accent}`}>{c.val}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading font-bold text-xl">{ffe ? "FF&E schedule" : "Material schedule"}</h2>
          <p className="text-xs text-zinc-500">Rev {cur.meta.rev} · {cur.items?.length || 0} item{(cur.items?.length || 0) !== 1 ? "s" : ""} in {spaces.length} space{spaces.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-sm h-9" data-testid="schedule-rev-btn">Rev {cur.meta.rev}</Button></AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader><AlertDialogTitle>Bump to the next revision?</AlertDialogTitle>
                <AlertDialogDescription>This issues a new revision letter and timestamps the schedule.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                <AlertDialogAction className="rounded-sm bg-zinc-900" onClick={bumpRev}>Bump revision</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={exportCsv} data-testid="schedule-export-csv"><FileDown className="h-4 w-4" /> Export CSV</Button>
          <Button className="rounded-sm h-9 gap-1.5 bg-zinc-900 hover:bg-zinc-800" onClick={() => setPrintOpen(true)} data-testid="schedule-export-pdf"><Printer className="h-4 w-4" /> Export PDF</Button>
          {canManage && <><span className="w-px h-6 bg-zinc-200" />
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => setManageOpen(true)} data-testid="schedule-manage-spaces"><LayoutGrid className="h-4 w-4" /> Manage spaces</Button>
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => setImportOpen(true)} data-testid="schedule-import"><Upload className="h-4 w-4" /> Import Excel</Button>
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => { setEdit({ ...EMPTY, kind, space_id: spaceFilter !== "all" && spaceFilter !== UNASSIGNED ? spaceFilter : "" }); setSaveToLib(false); }} data-testid="schedule-new-item"><Plus className="h-4 w-4" /> New item</Button>
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => setLibOpen(true)} data-testid="schedule-add-library"><LibraryIcon className="h-4 w-4" /> Add from library</Button></>}
        </div>
      </div>

      {sections.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="schedule-space-filter">
          {[{ id: "all", name: "All spaces" }, ...sections].map((s) => (
            <button key={s.id} onClick={() => setSpaceFilter(s.id)} className={`text-xs px-2.5 py-1 rounded-full border ${spaceFilter === s.id ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`} data-testid={`schedule-space-chip-${s.id}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div className="min-w-[860px]">
          <div className={`grid ${gridCols} px-3 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 items-center gap-2`}>
            <span></span><span>Code</span><span>Material / product</span><span>Location</span><span>Finish</span><span>Size</span><span>Qty</span>{ffe ? <><span>Rate</span><span>Total</span></> : <span>Unit</span>}<span>Status</span><span></span>
          </div>
          {loading ? <div className="p-8"><div className="h-6 w-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>
            : spaces.length === 0 && (cur.items || []).length === 0 ?
              <div className="p-6 text-sm text-zinc-400">
                No spaces yet. {canManage ? <>Click <strong>Manage spaces</strong> to add rooms/areas, then add products to each.</> : "No spaces have been set up yet."}
              </div>
              : visibleSections.map((sec) => {
                const secTotal = sec.items.reduce((a, i) => a + (i.rate_ex_gst || 0) * (i.qty || 0), 0);
                return (
                <div key={sec.id} data-testid={`schedule-section-${sec.id}`}>
                  <div className="bg-zinc-100 px-3 py-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{sec.name} <span className="text-zinc-400">{sec.items.length} item{sec.items.length !== 1 ? "s" : ""}</span></span>
                    {ffe && sec.items.length > 0 && <span className="text-xs font-semibold text-zinc-700 tabular-nums" data-testid={`schedule-section-total-${sec.id}`}>{money(secTotal)}</span>}
                  </div>
                  {sec.items.length === 0 ? <div className="px-3 py-2.5 text-xs text-zinc-400 border-b border-zinc-100">No products in this space yet.</div>
                    : sec.items.map((it) => {
                    const sm = STATUS[it.status] || STATUS.proposed;
                    return (
                      <div key={it.item_id} data-testid={`schedule-row-${it.item_id}`}
                        draggable={canManage}
                        onDragStart={(e) => { setDragId(it.item_id); dragSec.current = sec.id; if (e.dataTransfer) { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", it.item_id); } }}
                        onDragOver={(e) => { if (canManage && dragSec.current === sec.id) { e.preventDefault(); setDragOverId(it.item_id); } }}
                        onDragLeave={() => setDragOverId((v) => (v === it.item_id ? null : v))}
                        onDrop={() => { setDragOverId(null); onRowDrop(sec, it); }}
                        onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                        className={`grid ${gridCols} px-3 py-2.5 border-b border-zinc-100 items-center gap-2 text-sm ${dragId === it.item_id ? "opacity-40" : ""} ${dragOverId === it.item_id && dragId && dragId !== it.item_id ? "border-t-2 border-t-zinc-900" : ""}`}>
                        {canManage
                          ? <button className="text-zinc-300 hover:text-zinc-600 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`schedule-drag-${it.item_id}`}><GripVertical className="h-4 w-4" /></button>
                          : <input type="checkbox" data-testid={`schedule-check-${it.item_id}`} />}
                        <span className="text-[10px] font-bold bg-zinc-100 rounded-sm px-1.5 py-0.5 w-fit">{it.code}</span>
                        <span className="flex items-center gap-2 min-w-0">
                          {(it.images || [])[0] ? <img src={`${FILE_BASE}/${it.images[0].path}`} alt="" className="h-10 w-10 rounded-sm object-cover border border-zinc-200 shrink-0" /> : <span className="h-10 w-10 rounded-sm bg-zinc-100 shrink-0" />}
                          <span className="min-w-0"><span className="font-medium truncate block">{it.name}</span><span className="text-xs text-zinc-500 truncate block">{[it.supplier, it.product_code].filter(Boolean).join(" · ")}</span></span>
                        </span>
                        <span className="text-zinc-600 truncate">{it.location || "—"}</span>
                        <span className="text-zinc-600 truncate">{it.finish || "—"}</span>
                        <span className="text-zinc-600 truncate">{it.size || "—"}</span>
                        <span>{it.qty}</span>
                        {ffe ? <><span>{money(it.rate_ex_gst)}</span><span className="font-medium">{money((it.rate_ex_gst || 0) * (it.qty || 0))}</span></> : <span className="text-zinc-600">{it.unit}</span>}
                        <span className="flex items-center gap-1">
                          <Select value={it.status} onValueChange={(v) => patchRow(it, { status: v })} disabled={!canManage}>
                            <SelectTrigger className="h-7 rounded-sm text-xs font-semibold border-0 px-2" style={{ background: sm.bg, color: sm.color }} data-testid={`schedule-status-${it.item_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                          </Select>
                          {it.status === "approved" && <Lock className="h-3.5 w-3.5 text-emerald-600 shrink-0" title="Price and spec locked at approval" />}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button data-testid={`schedule-menu-${it.item_id}`} className="text-zinc-400 hover:text-zinc-900"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEdit({ ...it, space_id: it.space_id || "" }); setSaveToLib(false); }} disabled={!canManage}>Edit</DropdownMenuItem>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger disabled={!canManage} data-testid={`schedule-move-${it.item_id}`}>Move to space</DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                {spaces.map((s) => (
                                  <DropdownMenuItem key={s.space_id} disabled={s.space_id === it.space_id} onClick={() => patchRow(it, { space_id: s.space_id })} data-testid={`schedule-move-to-${s.space_id}`}>{s.name}</DropdownMenuItem>
                                ))}
                                {it.space_id && <DropdownMenuItem onClick={() => patchRow(it, { space_id: "" })} className="text-zinc-500">Unassign</DropdownMenuItem>}
                                {spaces.length === 0 && <DropdownMenuItem disabled>No spaces defined</DropdownMenuItem>}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuItem onClick={() => removeRow(it)} disabled={!canManage} className="text-rose-600">Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}
                </div>
                );
              })}
          {ffe && (cur.items || []).length > 0 && (
            <div className="px-3 py-3 border-t border-zinc-200 text-sm flex justify-end gap-8" data-testid="schedule-footer">
              <span className="text-zinc-500">Subtotal ex GST <strong className="text-zinc-900 ml-2">{money(cur.summary.subtotal_ex_gst)}</strong></span>
              <span className="text-zinc-500">GST 10% <strong className="text-zinc-900 ml-2">{money(cur.summary.gst)}</strong></span>
              <span className="text-zinc-500">Total inc GST <strong className="text-zinc-900 ml-2">{money(cur.summary.total_inc_gst)}</strong></span>
            </div>
          )}
        </div>
      </div>

      <EditDialog edit={edit} setEdit={setEdit} onSave={saveEdit} ffe={ffe} saveToLib={saveToLib} setSaveToLib={setSaveToLib} spaces={spaces} onCreateSpace={createSpace} />
      <AddFromLibrary open={libOpen} onOpenChange={setLibOpen} pid={pid} kind={kind} existing={(cur.items || []).map((i) => ({ product_id: i.product_id, space_id: i.space_id })).filter((x) => x.product_id)} onAdded={reload} ffe={ffe} spaces={spaces} onCreateSpace={createSpace} />
      <ManageSpacesDialog open={manageOpen} onOpenChange={setManageOpen} pid={pid} spaces={spaces} onCreateSpace={createSpace} onChanged={async () => { await loadKind("material"); await loadKind("ffe"); }} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} pid={pid} kind={kind} ffe={ffe} spaces={spaces} onImported={async () => { await loadKind("material"); await loadKind("ffe"); }} />

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="print-options">
          <DialogHeader><DialogTitle className="font-heading">Export PDF</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Copy</label>
              <Select value={printOpts.copy} onValueChange={(v) => setPrintOpts((o) => ({ ...o, copy: v }))}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="print-copy"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="client">Client (no prices)</SelectItem><SelectItem value="builder">Builder (with prices)</SelectItem></SelectContent>
              </Select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Images</label>
              <Select value={printOpts.images ? "yes" : "no"} onValueChange={(v) => setPrintOpts((o) => ({ ...o, images: v === "yes" }))}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="print-images"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
              </Select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Issued for</label>
              <Input className="mt-1.5 rounded-sm" value={printOpts.issuedFor} onChange={(e) => setPrintOpts((o) => ({ ...o, issuedFor: e.target.value }))} data-testid="print-issued-for" /></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setPrintOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-zinc-900" onClick={doPrint} data-testid="print-confirm">Open print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Dropdown of project spaces, with an inline "add new space" option.
function SpaceSelect({ value, onChange, spaces, onCreateSpace, testid }) {
  const handle = async (v) => {
    if (v === "__new__") {
      const name = window.prompt("New space name (e.g. Kitchen, Master Ensuite):");
      if (name && name.trim()) {
        try { const sp = await onCreateSpace(name.trim()); onChange(sp.space_id); }
        catch (e) { toast.error(apiErr(e, "Could not add space")); }
      }
      return;
    }
    onChange(v === "__none__" ? "" : v);
  };
  return (
    <Select value={value || "__none__"} onValueChange={handle}>
      <SelectTrigger className="mt-1.5 rounded-sm" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Unassigned</SelectItem>
        {spaces.map((s) => <SelectItem key={s.space_id} value={s.space_id}>{s.name}</SelectItem>)}
        <SelectItem value="__new__" className="text-zinc-900 font-semibold">＋ Add new space…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function EditDialog({ edit, setEdit, onSave, ffe, saveToLib, setSaveToLib, spaces, onCreateSpace }) {
  if (!edit) return null;
  const set = (p) => setEdit((e) => ({ ...e, ...p }));
  const L = "text-xs font-semibold uppercase tracking-wider text-zinc-500";
  const locked = edit.status === "approved" && edit.item_id;
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="schedule-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">{edit.item_id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={L}>Name</label><Input value={edit.name} onChange={(e) => set({ name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="schedule-edit-name" /></div>
          <div><label className={L}>Supplier</label><Input value={edit.supplier} onChange={(e) => set({ supplier: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Product code</label><Input value={edit.product_code} onChange={(e) => set({ product_code: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Space</label><SpaceSelect value={edit.space_id} onChange={(v) => set({ space_id: v })} spaces={spaces} onCreateSpace={onCreateSpace} testid="edit-space" /></div>
          <div><label className={L}>Category</label><Input value={edit.category} onChange={(e) => set({ category: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Location</label><Input value={edit.location} onChange={(e) => set({ location: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Finish {locked && "🔒"}</label><Input value={edit.finish} disabled={locked} onChange={(e) => set({ finish: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Size {locked && "🔒"}</label><Input value={edit.size} disabled={locked} onChange={(e) => set({ size: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Qty</label><Input type="number" value={edit.qty} onChange={(e) => set({ qty: e.target.value })} className="mt-1.5 rounded-sm" data-testid="edit-qty" /></div>
          <div><label className={L}>Unit</label><Input value={edit.unit} onChange={(e) => set({ unit: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          <div><label className={L}>Rate ex GST {locked && "🔒"}</label><Input type="number" value={edit.rate_ex_gst} disabled={locked} onChange={(e) => set({ rate_ex_gst: e.target.value })} className="mt-1.5 rounded-sm" data-testid="edit-rate" /></div>
          <div className="sm:col-span-2"><label className={L}>Notes</label><Textarea value={edit.notes} rows={2} onChange={(e) => set({ notes: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          {!edit.item_id && <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={saveToLib} onChange={(e) => setSaveToLib(e.target.checked)} data-testid="edit-save-to-library" /> Also save to Library</label>}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setEdit(null)}>Cancel</Button>
          <Button className="rounded-sm bg-zinc-900" onClick={onSave} data-testid="edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageSpacesDialog({ open, onOpenChange, pid, spaces, onCreateSpace, onChanged }) {
  const [newName, setNewName] = useState("");
  const [names, setNames] = useState({});
  const [templates, setTemplates] = useState([]);
  const [applyId, setApplyId] = useState("");
  useEffect(() => { if (open) setNames(Object.fromEntries(spaces.map((s) => [s.space_id, s.name]))); }, [open, spaces]);
  const loadTemplates = useCallback(() => {
    api.get("/schedules-space-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);
  useEffect(() => { if (open) { loadTemplates(); setApplyId(""); } }, [open, loadTemplates]);

  const applyTemplate = async () => {
    if (!applyId) return;
    try {
      const { data } = await api.post(`/projects/${pid}/schedules-spaces/apply-template`, { template_id: applyId });
      toast.success(data.added ? `Added ${data.added} space${data.added !== 1 ? "s" : ""}` : "All those spaces already exist");
      setApplyId(""); onChanged();
    } catch (e) { toast.error(apiErr(e, "Could not apply template")); }
  };
  const saveAsTemplate = async () => {
    if (!spaces.length) return;
    const name = window.prompt("Name this space template (e.g. Standard home):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/schedules-space-templates", { name: name.trim(), spaces: spaces.map((s) => s.name) });
      toast.success("Template saved"); loadTemplates();
    } catch (e) { toast.error(apiErr(e, "Could not save template")); }
  };
  const deleteTemplate = async (t) => {
    try { await api.delete(`/schedules-space-templates/${t.template_id}`); toast.success("Template deleted"); loadTemplates(); }
    catch (e) { toast.error("Could not delete template"); }
  };

  const add = async () => {
    if (!newName.trim()) return;
    try { await onCreateSpace(newName.trim()); setNewName(""); toast.success("Space added"); onChanged(); }
    catch (e) { toast.error(apiErr(e, "Could not add space")); }
  };
  const rename = async (s) => {
    const name = (names[s.space_id] || "").trim();
    if (!name || name === s.name) return;
    try { await api.patch(`/projects/${pid}/schedules-spaces/${s.space_id}`, { name }); onChanged(); }
    catch (e) { toast.error(apiErr(e, "Could not rename")); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= spaces.length) return;
    const arr = [...spaces];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.post(`/projects/${pid}/schedules-spaces/reorder`, { space_ids: arr.map((s) => s.space_id) }); onChanged(); }
    catch (e) { toast.error("Could not reorder"); }
  };
  const del = async (s) => {
    try { await api.delete(`/projects/${pid}/schedules-spaces/${s.space_id}`); toast.success("Space deleted"); onChanged(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto" data-testid="manage-spaces-dialog">
        <DialogHeader><DialogTitle className="font-heading">Manage spaces</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">Rooms / areas shared by both the Material and FF&amp;E schedules. Each becomes a section you can assign products to.</p>
        <div className="space-y-2">
          {spaces.length === 0 && <p className="text-sm text-zinc-400 py-2">No spaces yet — add your first below.</p>}
          {spaces.map((s, idx) => (
            <div key={s.space_id} className="flex items-center gap-1.5" data-testid={`manage-space-${s.space_id}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30 leading-none" data-testid={`space-up-${s.space_id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === spaces.length - 1} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30 leading-none" data-testid={`space-down-${s.space_id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <Input value={names[s.space_id] ?? s.name} onChange={(e) => setNames((n) => ({ ...n, [s.space_id]: e.target.value }))} onBlur={() => rename(s)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className="rounded-sm h-9 flex-1" data-testid={`space-name-${s.space_id}`} />
              <AlertDialog>
                <AlertDialogTrigger asChild><button className="text-zinc-400 hover:text-rose-600 p-1.5" data-testid={`space-delete-${s.space_id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                <AlertDialogContent className="rounded-sm">
                  <AlertDialogHeader><AlertDialogTitle>Delete “{s.name}”?</AlertDialogTitle>
                    <AlertDialogDescription>Any products in this space will become Unassigned. This affects both schedules.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="rounded-sm bg-rose-600 hover:bg-rose-700" onClick={() => del(s)}>Delete space</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-zinc-100">
          <Input placeholder="New space name…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} className="rounded-sm" data-testid="new-space-name" />
          <Button className="rounded-sm bg-zinc-900 gap-1.5" onClick={add} data-testid="add-space-btn"><Plus className="h-4 w-4" /> Add</Button>
        </div>

        <div className="pt-3 border-t border-zinc-100 space-y-2" data-testid="space-templates">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Templates</p>
          <div className="flex gap-2">
            <Select value={applyId} onValueChange={setApplyId}>
              <SelectTrigger className="rounded-sm flex-1" data-testid="template-select"><SelectValue placeholder={templates.length ? "Apply a saved set…" : "No templates yet"} /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name} · {t.spaces.length} space{t.spaces.length !== 1 ? "s" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-sm" onClick={applyTemplate} disabled={!applyId} data-testid="template-apply">Apply</Button>
          </div>
          {templates.length > 0 && (
            <div className="space-y-1">
              {templates.map((t) => (
                <div key={t.template_id} className="flex items-center justify-between text-xs text-zinc-500" data-testid={`template-row-${t.template_id}`}>
                  <span className="truncate">{t.name} <span className="text-zinc-400">({t.spaces.join(", ")})</span></span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="text-zinc-400 hover:text-rose-600 p-1 shrink-0" data-testid={`template-delete-${t.template_id}`}><Trash2 className="h-3.5 w-3.5" /></button></AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader><AlertDialogTitle>Delete the “{t.name}” template?</AlertDialogTitle>
                        <AlertDialogDescription>This removes it for the whole studio — other projects will no longer be able to apply it. Spaces already added to projects stay.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-rose-600 hover:bg-rose-700" onClick={() => deleteTemplate(t)}>Delete template</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" className="rounded-sm w-full gap-1.5 text-xs" onClick={saveAsTemplate} disabled={!spaces.length} data-testid="template-save-current"><LayoutGrid className="h-3.5 w-3.5" /> Save current spaces as a template</Button>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FIELD_LABELS = {
  code: "Code", name: "Name *", supplier: "Supplier", product_code: "Product code", category: "Category",
  space: "Space / Room", location: "Location", finish: "Finish", size: "Size", qty: "Qty",
  unit: "Unit", rate_ex_gst: "Rate ex GST", status: "Status", notes: "Notes",
};
const TEMPLATE_COLS = ["Code", "Name", "Supplier", "Product code", "Category", "Space", "Location", "Finish", "Size", "Qty", "Unit", "Rate ex GST", "Status", "Notes"];

function downloadTemplate() {
  const example = ["", "Pendant light", "Flos", "PL-200", "Lighting", "Kitchen", "Over island", "Brass", "300mm", "3", "each", "450", "proposed", ""];
  const csv = [TEMPLATE_COLS, example].map((r) => r.map((c) => `"${c}"`).join(",")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = "Schedule import template.csv";
  a.click();
}

function ImportDialog({ open, onOpenChange, pid, kind, ffe, spaces, onImported }) {
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [spaceMode, setSpaceMode] = useState("column");
  const [spaceId, setSpaceId] = useState("");
  const [replace, setReplace] = useState(false);
  const [matchUpdate, setMatchUpdate] = useState(false);
  const [matchBy, setMatchBy] = useState("code");
  const [summary, setSummary] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setStep("upload"); setPreview(null); setMapping({}); setSpaceMode("column"); setSpaceId(""); setReplace(false); setMatchUpdate(false); setMatchBy("code"); setSummary(null); } }, [open]);
  // If the Space column gets unmapped while mode is "column", fall back so rows aren't silently Unassigned.
  useEffect(() => { if (spaceMode === "column" && !mapping.space) setSpaceMode("none"); }, [mapping.space, spaceMode]);
  // Keep the match-on choice valid as columns get mapped/unmapped.
  useEffect(() => {
    if (matchBy === "code" && !mapping.code && mapping.product_code) setMatchBy("product_code");
    else if (matchBy === "product_code" && !mapping.product_code && mapping.code) setMatchBy("code");
    else if (matchBy === "both" && !(mapping.code && mapping.product_code)) setMatchBy(mapping.code ? "code" : "product_code");
  }, [mapping.code, mapping.product_code, matchBy]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/projects/${pid}/schedules/${kind}/import/preview`, fd);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setSpaceMode(data.suggested_mapping?.space ? "column" : "none");
      const hasCode = !!data.suggested_mapping?.code;
      const hasPc = !!data.suggested_mapping?.product_code;
      setMatchUpdate(hasCode || hasPc);
      setMatchBy(hasCode ? "code" : hasPc ? "product_code" : "code");
      setStep("map");
    } catch (err) { toast.error(apiErr(err, "Could not read that file")); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!mapping.name) { toast.error("Map the Name column first"); return; }
    if (spaceMode === "single" && !spaceId) { toast.error("Pick a space to assign rows to"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/schedules/${kind}/import`, {
        rows: preview.rows, mapping, space_mode: spaceMode,
        space_id: spaceMode === "single" ? spaceId : null, replace,
        match_update: matchUpdate && !replace, match_by: matchBy,
      });
      setSummary(data);
      setStep("done");
      onImported();
    } catch (err) { toast.error(apiErr(err, "Could not import")); }
    finally { setBusy(false); }
  };

  const cols = preview?.columns || [];
  const previewRows = (preview?.rows || []).slice(0, 5);
  const validCount = mapping.name ? (preview?.rows || []).filter((r) => String(r[mapping.name] || "").trim()).length : 0;
  const capped = preview && preview.total_rows > (preview.rows?.length || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import into the {ffe ? "FF&E" : "Material"} schedule</DialogTitle></DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="w-full border-2 border-dashed border-zinc-300 rounded-sm py-10 flex flex-col items-center gap-2 text-zinc-500 hover:border-zinc-400 hover:bg-zinc-50 transition-colors disabled:opacity-50" data-testid="import-dropzone">
              <Upload className="h-7 w-7" />
              <span className="text-sm font-semibold">{busy ? "Reading file…" : "Choose an Excel (.xlsx) or CSV file"}</span>
              <span className="text-xs">The first row should be column headings</span>
            </button>
            <button onClick={downloadTemplate} className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1.5" data-testid="import-template"><FileDown className="h-3.5 w-3.5" /> Download a template</button>
          </div>
        )}

        {step === "map" && preview && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">Found <strong>{preview.total_rows}</strong> rows. Check the column matches below — we’ve auto-filled them.</p>
            {capped && <p className="text-xs text-amber-600">Only the first {preview.rows.length} rows will be imported (large file).</p>}
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
              {preview.fields.map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <label className="text-xs text-zinc-600 w-28 shrink-0">{FIELD_LABELS[f] || f}</label>
                  <Select value={mapping[f] || "__none__"} onValueChange={(v) => setMapping((mm) => ({ ...mm, [f]: v === "__none__" ? undefined : v }))}>
                    <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`import-map-${f}`}><SelectValue placeholder="— not mapped —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— not mapped —</SelectItem>
                      {cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-100 pt-3 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Allocate to spaces</label>
                <Select value={spaceMode} onValueChange={setSpaceMode}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="import-space-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="column" disabled={!mapping.space}>Use the Space/Room column{!mapping.space ? " (map it above first)" : ""}</SelectItem>
                    <SelectItem value="single">Assign every row to one space</SelectItem>
                    <SelectItem value="none">Import all as Unassigned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {spaceMode === "single" && (
                <div><label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Space</label>
                  <Select value={spaceId || "__none__"} onValueChange={(v) => setSpaceId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid="import-space-id"><SelectValue placeholder="Pick a space" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Pick a space…</SelectItem>
                      {spaces.map((s) => <SelectItem key={s.space_id} value={s.space_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {spaces.length === 0 && <p className="text-xs text-amber-600 mt-1">No spaces yet — add some in Manage spaces, or use a Space column.</p>}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="import-replace-toggle">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="h-4 w-4 rounded-sm accent-rose-600" />
                Replace everything currently in this schedule
              </label>
              {(mapping.code || mapping.product_code) && !replace && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="import-match-toggle">
                    <input type="checkbox" checked={matchUpdate} onChange={(e) => setMatchUpdate(e.target.checked)} className="h-4 w-4 rounded-sm accent-zinc-900" />
                    Update items that already match (instead of adding duplicates)
                  </label>
                  {matchUpdate && (
                    <div className="flex items-center gap-2 pl-6">
                      <span className="text-xs text-zinc-500">Match on</span>
                      <Select value={matchBy} onValueChange={setMatchBy}>
                        <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="import-match-by"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {mapping.code && <SelectItem value="code">Schedule Code</SelectItem>}
                          {mapping.product_code && <SelectItem value="product_code">Supplier product code</SelectItem>}
                          {mapping.code && mapping.product_code && <SelectItem value="both">Either Code or product code</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {previewRows.length > 0 && mapping.name && (
              <div className="border border-zinc-200 rounded-sm overflow-x-auto">
                <table className="text-xs w-full"><thead><tr className="bg-zinc-50 text-left text-zinc-500">
                  <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Space</th><th className="px-2 py-1.5">Qty</th><th className="px-2 py-1.5">Rate</th></tr></thead>
                  <tbody>{previewRows.map((r, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="px-2 py-1.5">{r[mapping.name] || <span className="text-rose-500">(empty — skipped)</span>}</td>
                      <td className="px-2 py-1.5">{spaceMode === "column" ? (r[mapping.space] || "—") : spaceMode === "single" ? (spaces.find((s) => s.space_id === spaceId)?.name || "—") : "Unassigned"}</td>
                      <td className="px-2 py-1.5">{mapping.qty ? (r[mapping.qty] || "1") : "1"}</td>
                      <td className="px-2 py-1.5">{mapping.rate_ex_gst ? (r[mapping.rate_ex_gst] || "0") : "0"}</td>
                    </tr>
                  ))}</tbody></table>
              </div>
            )}
          </div>
        )}

        {step === "done" && summary && (
          <div className="space-y-4" data-testid="import-summary">
            <p className="text-sm text-zinc-500">Here’s what changed in the {ffe ? "FF&E" : "Material"} schedule{summary.replaced ? " (replaced)" : ""}.</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="summary-added">
                <div className="text-2xl font-heading text-emerald-700">{summary.imported}</div>
                <div className="text-xs text-emerald-700/80 uppercase tracking-wider">Added</div>
              </div>
              <div className="rounded-sm border border-sky-200 bg-sky-50 p-3 text-center" data-testid="summary-updated">
                <div className="text-2xl font-heading text-sky-700">{summary.updated}</div>
                <div className="text-xs text-sky-700/80 uppercase tracking-wider">Updated</div>
              </div>
              <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-center" data-testid="summary-skipped">
                <div className="text-2xl font-heading text-zinc-600">{summary.skipped}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Skipped</div>
              </div>
            </div>
            {summary.updated > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Updated items</div>
                <div className="max-h-32 overflow-y-auto border border-zinc-100 rounded-sm divide-y divide-zinc-100 text-xs">
                  {summary.updated_names.map((n, i) => <div key={i} className="px-2 py-1 truncate">{n}</div>)}
                </div>
              </div>
            )}
            {summary.imported > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">New items</div>
                <div className="max-h-32 overflow-y-auto border border-zinc-100 rounded-sm divide-y divide-zinc-100 text-xs">
                  {summary.added_names.map((n, i) => <div key={i} className="px-2 py-1 truncate">{n}</div>)}
                </div>
              </div>
            )}
            {summary.skipped > 0 && <p className="text-xs text-zinc-500">{summary.skipped} row{summary.skipped !== 1 ? "s were" : " was"} skipped (no product name).</p>}
          </div>
        )}

        <DialogFooter>
          {step !== "done" && <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>}
          {step === "map" && <Button className="rounded-sm bg-zinc-900" onClick={doImport} disabled={busy || !mapping.name} data-testid="import-confirm">{busy ? "Importing…" : `Import ${validCount} item${validCount !== 1 ? "s" : ""}`}</Button>}
          {step === "done" && <Button className="rounded-sm bg-zinc-900" onClick={() => onOpenChange(false)} data-testid="import-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AddFromLibrary({ open, onOpenChange, pid, kind, existing, onAdded, ffe, spaces, onCreateSpace }) {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sel, setSel] = useState([]);
  const [spaceId, setSpaceId] = useState("");
  useEffect(() => { if (open) { setSel([]); setSpaceId(""); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const params = { status: "active" };
    if (q.trim()) params.q = q.trim();
    if (cat !== "all") params.category = cat;
    api.get("/library/products", { params }).then((r) => { setProducts(r.data.products); setCats(r.data.categories); });
  }, [open, q, cat]);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const add = async () => {
    try { const { data } = await api.post(`/projects/${pid}/schedules/${kind}/from-library`, { product_ids: sel, space_id: spaceId || null }); toast.success(`Added ${data.count} items`); onAdded(); onOpenChange(false); }
    catch (e) { toast.error(apiErr(e, "Could not add")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-from-library">
        <DialogHeader><DialogTitle className="font-heading">Add from library</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm" data-testid="lib-search" />
          <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-40 rounded-sm" data-testid="lib-cat"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Assign to space</label>
          <SpaceSelect value={spaceId} onChange={setSpaceId} spaces={spaces} onCreateSpace={onCreateSpace} testid="lib-space" /></div>
        <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100 max-h-72 overflow-y-auto">
          {products.map((p) => {
            const already = existing.some((e) => e.product_id === p.product_id && (e.space_id || "") === (spaceId || ""));
            return (
              <label key={p.product_id} className={`flex items-center gap-3 p-2 text-sm ${already ? "opacity-50" : "cursor-pointer hover:bg-zinc-50"}`} data-testid={`lib-item-${p.product_id}`}>
                <input type="checkbox" disabled={already} checked={already || sel.includes(p.product_id)} onChange={() => toggle(p.product_id)} />
                {(p.images || [])[0]
                  ? <img src={`${FILE_BASE}/${p.images[0].path}`} alt="" className="h-10 w-10 rounded-sm object-cover border border-zinc-200 shrink-0" data-testid={`lib-item-thumb-${p.product_id}`} />
                  : <span className="h-10 w-10 rounded-sm bg-zinc-100 border border-zinc-200 shrink-0 flex items-center justify-center text-zinc-300"><Package className="h-4 w-4" /></span>}
                <span className="min-w-0 flex-1">
                  <span className="font-medium truncate block">{p.supplier || p.name}</span>
                  <span className="text-zinc-400 text-xs truncate block">{[p.supplier ? p.name : null, p.category].filter(Boolean).join(" · ")}{already ? " · already added" : ""}</span>
                </span>
              </label>
            );
          })}
          {products.length === 0 && <p className="p-4 text-sm text-zinc-400">No products found.</p>}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm bg-zinc-900" onClick={add} disabled={!sel.length} data-testid="lib-add-btn">Add {sel.length} items</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
